import * as React from 'react';
import { Body1AccentedTypography, Button, CaptionAccentTypography, Separator } from 'ui';
import { useSignupForm } from './useSignupForm';
import type { SignupFormValues } from './useSignupForm';
import { Banner } from '@components/atoms';
import { BaseInput, BrandHeader, LinkButton, PasswordInput } from '@components/molecules';
import type { BrandLogo } from '@components/molecules/brand-header';
import { TermsOfServiceLink } from '@components/molecules/terms-of-service-link';
import { SSOActionBar } from '@components/organisms/sso-action-bar';
import type { ResponseResult } from '@utils';
import { WithControl } from '@utils';

export type SignupLabels = {
	title: string;
	subtitle?: string;
	separator: string;
	email: string;
	password: string;
	submit: string;
	linkText: string;
	linkButton: string;
	firstName: string;
	lastName: string;
	tosText: string;
	tosButton: string;
	passwordErrors: {
		length: string;
		uppercase: string;
		lowercase: string;
		number: string;
	};
};

type Props = {
	labels: SignupLabels;
	logo?: BrandLogo;
	tosLink: string;
	onSubmit: (data: SignupFormValues) => Promise<ResponseResult<any>>;
	handleNavigateToLink: () => void;
	onSSOSuccess: () => void;
};

export const SignupForm: React.FC<Props> = ({
	labels,
	logo,
	handleNavigateToLink,
	onSubmit,
	tosLink,
	onSSOSuccess,
}) => {
	const { control, isValid, isSubmitted, handleSubmit, errors, isLoading } = useSignupForm({ onSubmit, labels });

	const [error, setError] = React.useState<string>();

	const ControlledPasswordInput = WithControl(PasswordInput, control);
	const ControlledEmailInput = WithControl(BaseInput, control);
	const ControlledFirstNameInput = WithControl(BaseInput, control);
	const ControlledLastNameInput = WithControl(BaseInput, control);

	const handleSSOError = (error: Error) => {
		if (!error) {
			return;
		}

		setError(error.message);
	};

	const submit = (e: React.FormEvent<HTMLFormElement>) => {
		setError(undefined);

		handleSubmit(e);
	};

	return (
		<div className="flex w-full flex-col gap-[2rem]" data-testid="auth-signup-form">
			<BrandHeader title={labels.title} subtitle={labels.subtitle} logo={logo} />
			<SSOActionBar onSuccess={onSSOSuccess} onError={handleSSOError} />

			<div className="flex items-center gap-2 whitespace-nowrap">
				<Separator />
				<CaptionAccentTypography className="text-primary" truncate={false}>
					{labels.separator}
				</CaptionAccentTypography>
				<Separator />
			</div>
			<form onSubmit={submit}>
				<div className="flex flex-col gap-[1.6rem] pb-[1.2rem]">
					{((errors.email?.message && isSubmitted) || error) && (
						<Banner title={errors?.email?.message || error} variant="error" />
					)}
					<ControlledEmailInput label={labels.email} name="email" type="email" data-aid="email-input" />
					<div className="flex flex-row gap-[0.75rem]">
						<ControlledFirstNameInput
							name="firstName"
							type="text"
							label={labels.firstName!}
							data-aid="first-name-input"
						/>
						<ControlledLastNameInput name="lastName" type="text" label={labels.lastName!} data-aid="last-name-input" />
					</div>
					<ControlledPasswordInput label={labels.password} name="password" error={errors.password?.message} />
				</div>
				<div className="flex flex-col gap-[0.75rem]">
					<TermsOfServiceLink link={tosLink} text={labels.tosText} linkText={labels.tosButton} />
					<Button
						variant="primary"
						className="rounded-2xl bg-brandHeader"
						type="submit"
						disabled={!isValid}
						isLoading={isLoading}
						data-testid="auth-signup-submit"
					>
						<Body1AccentedTypography>{labels.submit}</Body1AccentedTypography>
					</Button>
					{handleNavigateToLink && (
						<LinkButton linkText={labels.linkText} buttonText={labels.linkButton} onClick={handleNavigateToLink} />
					)}
				</div>
			</form>
		</div>
	);
};
